import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  HelpCircle, 
  X, 
  CheckCircle2,
  Info,
  Lightbulb,
} from 'lucide-react';
import { Button, IconGhostButton } from './ui/button';
import { DefaultInstructions } from './PageInstructions';
import { useInstructionEngine } from '../hooks/useInstructionEngine';
import type { PageInstructionEngineItemDto, PageRouteKey } from '../types/pageInstruction';
import apiClient from '../services/apiClient';
import type { ApiResponse, UiSettings } from '../types';
import { cn } from '@/lib/utils';
import { useActivePageInstructionContext } from './PageInstructionRenderContext';

// ─────────────────────────────────────────────────────────────
// Page Instruction Banner Component
// Collapsible instruction panel for contextual user guidance
// ─────────────────────────────────────────────────────────────

interface PageInstructionBannerProps {
  /**
   * Page route key to fetch instruction for.
   */
  pageRouteKey: PageRouteKey | string;
  
  /**
   * Program ID for program-specific instructions.
   */
  programId?: string | null;
  
  /**
   * Role ID for role-specific instructions.
   */
  roleId?: string | null;
  
  /**
   * Additional CSS classes.
   */
  className?: string;
  
  /**
   * Whether to show the banner (default: true).
   */
  enabled?: boolean;
  
  /**
   * Custom fallback instruction if none from API.
   */
  fallbackInstruction?: {
    title: string;
    purpose: string;
    keyActions?: string[];
    requiredSteps?: string[];
    additionalNotes?: string;
  };
}

/**
 * Page Instruction Banner Component
 * 
 * Displays contextual guidance for users on each page.
 * Features:
 * - Expandable/collapsible panel
 * - "Don't show again" option
 * - Persists user preference
 * - Supports light/dark mode
 * - Role and program aware
 * 
 * @example
 * ```tsx
 * <PageInstructionBanner
 *   pageRouteKey={PAGE_ROUTE_KEYS.WORKSPACE}
 *   programId={programId}
 * />
 * ```
 */
export function PageInstructionBanner({
  pageRouteKey,
  programId,
  roleId,
  className,
  enabled = true,
  fallbackInstruction,
}: PageInstructionBannerProps) {
  const location = useLocation();
  const activeInstructionContext = useActivePageInstructionContext();
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const {
    data: uiSettingsResponse,
    isLoading: isUiSettingsLoading,
  } = useQuery<ApiResponse<UiSettings>, Error>({
    queryKey: ['applicationUiSettings', 'pageInstructionsVisibility'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<UiSettings>>('/superadmin/settings/ui');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const showPageInstructions = uiSettingsResponse?.data.showPageInstructions ?? false;
  const instructionQueryEnabled = enabled && showPageInstructions;
  const isWorkspaceRoute = /\/workspace(\/|$)/.test(location.pathname);
  const isNestedDuplicate =
    activeInstructionContext?.pageRouteKey === pageRouteKey
    && (activeInstructionContext.programId ?? null) === (programId ?? null);

  const {
    data,
    shouldShow,
    isExpanded,
    isDismissed,
    isFirstVisit,
    isLoading,
    toggleCollapsed,
    dontShowAgain,
  } = useInstructionEngine({
    pageRouteKey,
    programId,
    roleId,
    enabled: instructionQueryEnabled && !isWorkspaceRoute && !isNestedDuplicate,
  });

  if (isWorkspaceRoute || isNestedDuplicate) {
    return null;
  }

  // Don't render if should not show or loading
  if (!instructionQueryEnabled || isUiSettingsLoading || isLoading) {
    return null;
  }

  // Honor "don't show again" even when fallbackInstruction is provided
  if (isDismissed) {
    return null;
  }

  // Use fallback if no instruction from API
  const instruction = data?.primaryInstruction ?? (fallbackInstruction ? {
    pageInstructionId: 'fallback',
    programId: programId ?? null,
    pageRouteKey,
    roleId: roleId ?? null,
    roleName: null,
    contextKey: null,
    title: fallbackInstruction.title,
    purpose: fallbackInstruction.purpose,
    keyActions: fallbackInstruction.keyActions ?? [],
    requiredSteps: fallbackInstruction.requiredSteps ?? [],
    additionalNotes: fallbackInstruction.additionalNotes ?? null,
    isActive: true,
    displayOrder: 0,
    resolutionScope: 'fallback',
    contextMatched: false,
    resolutionPriority: 0,
  } : null);

  const secondaryInstructions = (data?.instructions ?? []).filter((item) => {
    return item.pageInstructionId !== instruction?.pageInstructionId;
  });

  // Don't show if preference is "don't show again" and no fallback
  if (!shouldShow && !fallbackInstruction) {
    return null;
  }

  // Don't show if no instruction content
  if (!instruction) {
    return null;
  }

  const handleDismiss = () => {
    setShowDismissConfirm(true);
  };

  const confirmDismiss = () => {
    dontShowAgain();
    setShowDismissConfirm(false);
  };

  const cancelDismiss = () => {
    setShowDismissConfirm(false);
  };

  const headerActions = (
    <IconGhostButton
      icon={X}
      tooltip="Don't show again"
      onClick={(e) => {
        e.stopPropagation();
        handleDismiss();
      }}
      className="h-5 w-5 p-0 text-muted-foreground hover:bg-muted/60"
    />
  );

  return (
    <div className={cn('mb-4', className)}>
      {showDismissConfirm && (
        <div className="mb-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 dark:border-warning/20 dark:bg-warning/10">
          <div className="flex items-center justify-between">
            <span className="text-xs text-warning dark:text-warning">
              Hide this guidance? You can restore it from your profile settings.
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={cancelDismiss}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                className="h-6 px-2 text-xs bg-warning hover:bg-warning/90"
                onClick={confirmDismiss}
              >
                Hide
              </Button>
            </div>
          </div>
        </div>
      )}

      <DefaultInstructions
        title={instruction.title}
        open={isExpanded}
        onOpenChange={() => toggleCollapsed()}
        className={cn(
          'border-primary/20 bg-muted/20 dark:border-primary/30 dark:bg-primary/5',
          showDismissConfirm && 'opacity-70',
        )}
        headerClassName="px-3 py-1.5"
        contentClassName="px-3"
        headerActions={headerActions}
        icon={
          <div className="rounded-full bg-primary-soft p-1 dark:bg-primary/20">
            <HelpCircle className="h-3.5 w-3.5 text-primary" />
          </div>
        }
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {isFirstVisit && (
              <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                New
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">Expand for guided steps and page context.</span>
          </div>

          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-info mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              {instruction.purpose}
            </p>
          </div>

          {instruction.keyActions && instruction.keyActions.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Lightbulb className="w-3 h-3 text-warning" />
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Key Actions
                </span>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 pl-4">
                {instruction.keyActions.map((action, idx) => (
                  <li 
                    key={idx} 
                    className="flex items-start gap-1.5 text-xs text-muted-foreground"
                  >
                    <span className="text-primary mt-0.5">•</span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {instruction.requiredSteps && instruction.requiredSteps.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-success" />
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Steps to Complete
                </span>
              </div>
              <ol className="space-y-0.5 pl-4">
                {instruction.requiredSteps.map((step, idx) => (
                  <li 
                    key={idx} 
                    className="flex items-start gap-1.5 text-xs text-muted-foreground"
                  >
                    <span className="text-success font-medium min-w-[16px]">
                      {idx + 1}.
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {instruction.additionalNotes && (
            <div className="flex items-start gap-2 px-2 py-1.5 bg-primary-soft/50 dark:bg-primary/10 rounded">
              <Info className="w-3 h-3 text-info mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-primary dark:text-primary/80 italic">
                {instruction.additionalNotes}
              </p>
            </div>
          )}

          {secondaryInstructions.length > 0 && (
            <div className="space-y-1.5 border-t border-border/60 pt-2">
              <div className="flex items-center gap-1.5">
                <Lightbulb className="w-3 h-3 text-warning" />
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Additional Guidance
                </span>
              </div>
              <div className="space-y-2">
                {secondaryInstructions.map((item) => (
                  <SecondaryInstructionSummary key={item.pageInstructionId} instruction={item} />
                ))}
              </div>
            </div>
          )}
        </div>
      </DefaultInstructions>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Compact Version for Sidebars/Panels
// ─────────────────────────────────────────────────────────────

interface PageInstructionCompactProps {
  pageRouteKey: PageRouteKey | string;
  programId?: string | null;
  className?: string;
}

/**
 * Compact page instruction for sidebars and panels.
 */
export function PageInstructionCompact({
  pageRouteKey,
  programId,
  className,
}: PageInstructionCompactProps) {
  const {
    data: uiSettingsResponse,
    isLoading: isUiSettingsLoading,
  } = useQuery<ApiResponse<UiSettings>, Error>({
    queryKey: ['applicationUiSettings', 'pageInstructionsVisibility'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<UiSettings>>('/superadmin/settings/ui');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const showPageInstructions = uiSettingsResponse?.data.showPageInstructions ?? false;

  const { data, shouldShow, isLoading } = useInstructionEngine({
    pageRouteKey,
    programId,
    enabled: showPageInstructions,
  });

  if (!showPageInstructions || isUiSettingsLoading || !shouldShow || isLoading || !data?.primaryInstruction) {
    return null;
  }

  return (
    <div
      className={cn(
        'p-2 rounded bg-primary-soft/50 dark:bg-primary/5 border border-primary/20 dark:border-primary/30',
        className
      )}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <HelpCircle className="w-3 h-3 text-primary" />
        <span className="text-[11px] font-medium text-primary dark:text-primary/80">
          Quick Tip
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        {data.primaryInstruction.purpose}
      </p>
    </div>
  );
}

function SecondaryInstructionSummary({ instruction }: { instruction: PageInstructionEngineItemDto }) {
  return (
    <div className="rounded border border-border/60 bg-background/70 px-2 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-foreground">{instruction.title}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
          {formatResolutionScope(instruction)}
        </span>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{instruction.purpose}</p>
    </div>
  );
}

function formatResolutionScope(instruction: PageInstructionEngineItemDto) {
  if (instruction.contextMatched && instruction.contextKey) {
    return `Context: ${instruction.contextKey}`;
  }

  return instruction.resolutionScope
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export default PageInstructionBanner;
